import { ArrowRight, CalendarClock, Lock, Users } from "lucide-react";
import type { ReactNode } from "react";

import { ButtonLink } from "@/components/button-link";
import { useNow } from "@/hooks/use-now";
import type { Me } from "@/lib/api";
import { formatRelative, formatTimeLeft, formatTimestamp } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Where a student stands on teams, and how long they have to do something about it.
 *
 * On the home page it exists for one reason: to tell a student with no team that the
 * door is closing, and where to go. A student who has a team is not nagged there. On
 * the team page it is the status line for everyone -- the countdown while changes are
 * open, the lock once they are not -- because that page is where the clock matters.
 *
 * The countdown ticks. The server said whether changes were open when `/me` was
 * fetched; once the clock here passes the lock, the notice flips without a refetch,
 * since the server would refuse the next change anyway.
 */
export function TeamNotice({ me, where }: { me: Me; where: "home" | "team" }) {
  // Decide whether there is anything to show before starting a clock: a staff member, or
  // a student with a team on the home page, gets nothing, and should not pay a re-render
  // a second for it. Without a lock date nothing here changes with time either.
  if (me.role === "staff") return null;
  if (where === "home" && me.team) return null;
  if (me.team_policy.lock_at === null) return <Body me={me} where={where} now={0} />;
  return <Ticking me={me} where={where} />;
}

function Ticking({ me, where }: { me: Me; where: "home" | "team" }) {
  const now = useNow(1000);
  return <Body me={me} where={where} now={now} />;
}

function Body({ me, where, now }: { me: Me; where: "home" | "team"; now: number }) {
  const { lock_at: lockAt, max_size: maxSize } = me.team_policy;
  const open = me.team_policy.open && (lockAt === null || now < lockAt);
  const team = me.team;

  if (!open) {
    if (where === "home") return null;
    return (
      <Notice tone="muted" icon={<Lock className="size-4 shrink-0" aria-hidden />}>
        <span className="font-medium">Team changes are closed for {me.course.code}</span>
        <span className="text-muted-foreground">
          {lockAt !== null && now >= lockAt ? ` · closed ${formatRelative(lockAt)}` : ""}
          {" · "}
          {team ? `${team.name} is final.` : "Without a team you cannot enter a competition."} Ask the
          teaching team if something needs fixing.
        </span>
      </Notice>
    );
  }

  // The moment changes close, if one is set, and how far off it is right now.
  const closing = lockAt === null ? null : { at: formatTimestamp(lockAt), left: formatTimeLeft(lockAt, now) };
  const countdown = closing ? (
    <>
      Team changes close in <span className="tabular-nums">{closing.left}</span>
    </>
  ) : null;

  if (!team) {
    if (where === "home") {
      return (
        <Notice
          tone="amber"
          icon={<Users className="size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />}
          action={
            <ButtonLink to="/team" size="sm" variant="outline">
              Find teammates
              <ArrowRight className="size-4" aria-hidden />
            </ButtonLink>
          }
        >
          <span className="font-medium">You are not on a team yet</span>
          <span className="text-muted-foreground">
            {" · "}
            {closing ? (
              <>
                {countdown} ({closing.at})
              </>
            ) : (
              `Teams are up to ${maxSize}, and a competition is entered as a team — of one, if you like`
            )}
          </span>
        </Notice>
      );
    }
    if (!closing) return null;
    return (
      <Notice
        tone="amber"
        icon={<CalendarClock className="size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />}
      >
        <span className="font-medium">{countdown}</span>
        <span className="text-muted-foreground">
          {" · "}
          {closing.at} · form a team before then — with classmates, or of just you — to enter the competition.
        </span>
      </Notice>
    );
  }

  if (where === "home" || !closing) return null;
  return (
    <Notice tone="primary" icon={<CalendarClock className="size-4 shrink-0 text-primary" aria-hidden />}>
      <span className="font-medium">{countdown}</span>
      <span className="text-muted-foreground">
        {" · "}
        {closing.at} · after that, {team.name} is final.
      </span>
    </Notice>
  );
}

function Notice({
  tone,
  icon,
  action,
  children,
}: {
  tone: "amber" | "primary" | "muted";
  icon: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-4 py-3 text-sm",
        tone === "amber" && "border-amber-500/40 bg-amber-500/8",
        tone === "primary" && "border-primary/30 bg-primary/5",
        tone === "muted" && "bg-muted/40",
      )}
    >
      {icon}
      <p className="min-w-0 flex-1">{children}</p>
      {action}
    </div>
  );
}
